<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import BroadcastsApi from 'dashboard/api/broadcasts';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

const emit = defineEmits(['changed']);
const { t } = useI18n();
const { accountId } = useAccount();
const entries = ref([]);
const isExpanded = ref(false);
const inputType = ref('conversation');
const values = ref('');
const search = ref('');
const isLoading = ref(true);
const isAdding = ref(false);
const deletingId = ref(null);
const loadFailed = ref(false);
const addError = ref('');

const inputTypes = ['conversation', 'contact', 'email', 'phone'];
const filteredEntries = computed(() => {
  const query = search.value.trim().toLowerCase();
  return entries.value.filter(({ contact }) =>
    [
      contact.id,
      contact.name,
      contact.email,
      contact.phone_number,
      contact.identifier,
    ].some(value =>
      String(value || '')
        .toLowerCase()
        .includes(query)
    )
  );
});

const fetchBlacklist = async () => {
  isLoading.value = true;
  loadFailed.value = false;
  try {
    const { data } = await BroadcastsApi.getBlacklist();
    entries.value = data;
  } catch {
    loadFailed.value = true;
    useAlert(t('BROADCASTS.BLACKLIST.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const addContacts = async () => {
  isAdding.value = true;
  addError.value = '';
  try {
    const { data } = await BroadcastsApi.addToBlacklist(
      inputType.value,
      values.value
    );
    values.value = '';
    useAlert(
      t('BROADCASTS.BLACKLIST.ADDED', {
        added: data.added_count,
        existing: data.existing_count,
      })
    );
    emit('changed');
    await fetchBlacklist();
  } catch (error) {
    addError.value =
      error.response?.data?.message || t('BROADCASTS.BLACKLIST.ADD_FAILED');
  } finally {
    isAdding.value = false;
  }
};

const removeContact = async entry => {
  deletingId.value = entry.id;
  try {
    await BroadcastsApi.removeFromBlacklist(entry.id);
    entries.value = entries.value.filter(item => item.id !== entry.id);
    emit('changed');
    useAlert(t('BROADCASTS.BLACKLIST.REMOVED'));
  } catch {
    useAlert(t('BROADCASTS.BLACKLIST.REMOVE_FAILED'));
  } finally {
    deletingId.value = null;
  }
};

const contactRoute = id =>
  frontendURL(`accounts/${accountId.value}/contacts/${id}`);

onMounted(fetchBlacklist);
</script>

<template>
  <section
    class="mb-6 flex flex-col gap-4 rounded-xl border border-n-strong bg-n-solid-2 p-5"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 class="text-lg font-medium text-n-slate-12">
          {{ t('BROADCASTS.BLACKLIST.TITLE') }}
        </h2>
        <span v-if="!isLoading && !loadFailed" class="text-xs text-n-slate-11">
          {{ t('BROADCASTS.BLACKLIST.COUNT', { count: entries.length }) }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <Button
          type="button"
          icon="i-lucide-refresh-cw"
          variant="ghost"
          size="sm"
          :label="t('BROADCASTS.REFRESH')"
          :is-loading="isLoading"
          :disabled="isLoading || isAdding || deletingId !== null"
          @click="fetchBlacklist"
        />
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="sm"
          :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          :label="
            t(
              isExpanded
                ? 'BROADCASTS.BLACKLIST.COLLAPSE'
                : 'BROADCASTS.BLACKLIST.EXPAND'
            )
          "
          :aria-expanded="isExpanded"
          aria-controls="broadcast-blacklist-content"
          @click="isExpanded = !isExpanded"
        />
      </div>
    </div>

    <div
      v-if="isExpanded"
      id="broadcast-blacklist-content"
      class="flex flex-col gap-4"
    >
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('BROADCASTS.BLACKLIST.DESCRIPTION') }}
      </p>
      <form class="flex flex-col gap-3" @submit.prevent="addContacts">
        <div class="flex flex-col gap-1">
          <label
            for="blacklist-input-type"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('BROADCASTS.BLACKLIST.INPUT_TYPE') }}
          </label>
          <select
            id="blacklist-input-type"
            v-model="inputType"
            class="mb-0 w-full rounded-lg border border-n-weak bg-n-solid-2 text-sm text-n-slate-12 sm:w-80"
            :disabled="isAdding"
            @change="addError = ''"
          >
            <option v-for="type in inputTypes" :key="type" :value="type">
              {{ t(`BROADCASTS.BLACKLIST.TYPES.${type.toUpperCase()}`) }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label
            for="blacklist-values"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('BROADCASTS.BLACKLIST.VALUES') }}
          </label>
          <textarea
            id="blacklist-values"
            v-model="values"
            rows="3"
            class="mb-0 w-full rounded-lg border border-n-weak bg-n-alpha-black2 p-3 text-sm text-n-slate-12"
            :placeholder="
              t(`BROADCASTS.BLACKLIST.PLACEHOLDERS.${inputType.toUpperCase()}`)
            "
            :disabled="isAdding"
            :aria-invalid="!!addError"
            aria-describedby="blacklist-values-help blacklist-add-error"
            @input="addError = ''"
          />
          <p id="blacklist-values-help" class="mb-0 text-xs text-n-slate-11">
            {{ t('BROADCASTS.BLACKLIST.INPUT_HELP') }}
          </p>
          <p
            v-if="addError"
            id="blacklist-add-error"
            role="alert"
            class="mb-0 text-sm text-n-ruby-11"
          >
            {{ addError }}
          </p>
        </div>
        <Button
          type="submit"
          icon="i-lucide-user-minus"
          class="self-start"
          :label="t('BROADCASTS.BLACKLIST.ADD')"
          :is-loading="isAdding"
          :disabled="!values.trim() || isAdding || deletingId !== null"
        />
      </form>

      <div class="border-t border-n-strong pt-4">
        <div class="mb-3 flex justify-end">
          <Input
            id="blacklist-search"
            v-model="search"
            type="search"
            :placeholder="t('BROADCASTS.BLACKLIST.SEARCH')"
            :aria-label="t('BROADCASTS.BLACKLIST.SEARCH')"
            class="w-full sm:w-80"
          />
        </div>
        <p v-if="isLoading" class="text-sm text-n-slate-11">
          {{ t('BROADCASTS.LOADING') }}
        </p>
        <p v-else-if="loadFailed" role="alert" class="text-sm text-n-ruby-11">
          {{ t('BROADCASTS.BLACKLIST.LOAD_FAILED') }}
        </p>
        <p v-else-if="!entries.length" class="text-sm text-n-slate-11">
          {{ t('BROADCASTS.BLACKLIST.EMPTY') }}
        </p>
        <p v-else-if="!filteredEntries.length" class="text-sm text-n-slate-11">
          {{ t('BROADCASTS.BLACKLIST.NO_MATCHES') }}
        </p>
        <ul
          v-else
          class="m-0 max-h-80 list-none divide-y divide-n-weak overflow-y-auto p-0"
        >
          <li
            v-for="entry in filteredEntries"
            :key="entry.id"
            class="flex items-center justify-between gap-4 py-3"
          >
            <div class="min-w-0">
              <router-link
                :to="contactRoute(entry.contact.id)"
                class="break-words text-sm font-medium text-n-blue-11 hover:underline"
              >
                {{
                  entry.contact.name ||
                  t('BROADCASTS.BLACKLIST.CONTACT', { id: entry.contact.id })
                }}
              </router-link>
              <p class="mb-0 mt-1 break-words text-xs text-n-slate-11">
                {{
                  [
                    entry.contact.email,
                    entry.contact.phone_number,
                    entry.contact.identifier,
                  ]
                    .filter(Boolean)
                    .join(' · ')
                }}
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              color="ruby"
              size="sm"
              :label="t('BROADCASTS.BLACKLIST.REMOVE')"
              :is-loading="deletingId === entry.id"
              :disabled="deletingId !== null || isAdding"
              @click="removeContact(entry)"
            />
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>
